import { useState, useEffect } from "react";
import { getUserRole, getUserData } from "../utils/Auth";
import Button from "@mui/material/Button";
import { useTheme } from "../context/ThemeContext"; // 🔥 Import useTheme for dark mode
import API_BASE_URL from "../config";

const EditCustomerModal = ({
  isOpen,
  onClose,
  customer,
  onCustomerUpdated,
}) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [county, setCounty] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [notes, setNotes] = useState("");
  const [agentId, setAgentId] = useState("");
  const [agents, setAgents] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [type, setType] = useState(0);
  const [userData, setUserData] = useState(null);

  const userRole = getUserRole();
  const { darkMode } = useTheme();

  useEffect(() => {
    if (isOpen) {
      fetchUserData();
    }
  }, [isOpen]);

  const fetchUserData = async () => {
    const user = await getUserData();
    setUserData(user);
  };

  useEffect(() => {
    if (customer) {
      setName(customer.name);
      setPhone(customer.phone);
      setCity(customer.city);
      setCounty(customer.county);
      setBusinessType(customer.businessType);
      setNotes(customer.notes);
      setAgentId(customer.agent?.id || "");
      setType(customer.type !== undefined ? Number(customer.type) : 0);
    }
  }, [customer]);

  useEffect(() => {
    if (userRole === "Admin" && isOpen) {
      fetchAgents();
    }
  }, [isOpen, userRole]);

  const fetchAgents = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error("Eroare la încărcarea agenților.");
      }

      const data = await response.json();
      setAgents(data);
    } catch (err) {
      console.error("Hiba az ügynökök lekérésekor:", err);
    }
  };

  if (!isOpen || !customer) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");

      const apiUrl =
        userRole === "Admin"
          ? `${API_BASE_URL}/api/customers/admin/${customer.id}`
          : `${API_BASE_URL}/api/customers/${customer.id}`;

      const payload = {
        name,
        phone,
        city,
        county,
        businessType,
        notes,
        type: parseInt(type, 10),
      };

      if (userRole === "Admin") {
        payload.agentId = agentId;
      }

      const response = await fetch(apiUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Eroare la actualizarea clientului.");
      }

      setSuccessMessage("✅ Client actualizat cu succes!");
      setTimeout(() => {
        setSuccessMessage("");
        onCustomerUpdated();
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.id === "modal-overlay") {
      onClose();
    }
  };

  return (
    <div
      id="modal-overlay"
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80"
      onClick={handleOverlayClick}
    >
      <div
        className={`${
          darkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"
        } p-6 rounded-3xl shadow-xl w-[600px] relative`}
      >
        {/* 🔥 Bezárás "X" gombbal */}
        <button
          onClick={onClose}
          className={`absolute top-2 right-3 ${
            darkMode
              ? "text-red-500 hover:text-red-700"
              : "text-red-700 hover:text-red-500"
          } text-2xl`}
        >
          ✖
        </button>

        <h2
          className={`text-2xl font-semibold mb-4 text-center ${
            darkMode ? "text-blue-400" : "text-blue-600"
          }`}
        >
          Editare Client # {customer.name}
        </h2>

        {successMessage && (
          <p
            className={`${
              darkMode
                ? "text-green-500 bg-green-900"
                : "text-green-900 bg-green-200"
            } p-2 rounded mb-3 text-center`}
          >
            {successMessage}
          </p>
        )}

        {error && (
          <p
            className={`${
              darkMode ? "text-red-500 bg-red-900" : "text-red-900 bg-red-200"
            } p-2 rounded mb-3 text-center`}
          >
            {error}
          </p>
        )}

        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
          {/* 🔥 Nume */}
          <div>
            <label
              className={`block font-semibold mb-1 ${
                darkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Nume
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`w-full p-2 border rounded-2xl ${
                darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-300 bg-white text-black"
              }`}
              // required
            />
          </div>

          {/* 🔥 Telefon */}
          <div>
            <label
              className={`block font-semibold mb-1 ${
                darkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Nr. Telefon
            </label>
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={`w-full p-2 border rounded-2xl ${
                darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-300 bg-white text-black"
              }`}
              // required
            />
          </div>

          {/* 🔥 Oraș */}
          <div>
            <label
              className={`block font-semibold mb-1 ${
                darkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Oraș
            </label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className={`w-full p-2 border rounded-2xl ${
                darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-300 bg-white text-black"
              }`}
              // required
            />
          </div>

          {/* 🔥 Județ */}
          <div>
            <label
              className={`block font-semibold mb-1 ${
                darkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Județ
            </label>
            <input
              type="text"
              value={county}
              onChange={(e) => setCounty(e.target.value)}
              className={`w-full p-2 border rounded-2xl ${
                darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-300 bg-white text-black"
              }`}
              // required
            />
          </div>

          {/* 🔥 Tip Afacere */}
          <div>
            <label
              className={`block font-semibold mb-1 ${
                darkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Tip Afacere
            </label>
            <input
              type="text"
              value={businessType}
              onChange={(e) => setBusinessType(e.target.value)}
              className={`w-full p-2 border rounded-2xl ${
                darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-300 bg-white text-black"
              }`}
            />
          </div>

          {/* ✅ Csak akkor mutassuk meg a mezőt, ha userData be van töltve ÉS van jogosultsága */}
          {userData !== null &&
            (userRole === "Admin" || userData.canAccessMarmurist) && (
              <div>
                <label
                  className={`block font-semibold mb-1 ${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Tip Client
                </label>
                <select
                  value={type}
                  onChange={(e) => setType(Number(e.target.value))}
                  className={`w-full p-2 border rounded-2xl ${
                    darkMode
                      ? "border-gray-600 bg-gray-800 text-white"
                      : "border-gray-300 bg-white text-black"
                  }`}
                >
                  <option value={0}>Client Final</option>
                  <option value={1}>Client Marmist</option>
                </select>
              </div>
            )}

          {/* 🔥 Notițe */}
          <div className="col-span-2">
            <label
              className={`block font-semibold mb-1 ${
                darkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Notițe
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={`w-full p-2 border rounded-2xl ${
                darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-300 bg-white text-black"
              } resize-none h-24`}
            ></textarea>
          </div>

          {/* 🔥 Gombok */}
          <div className="col-span-2 flex justify-between gap-4 mt-4">
            <Button
              type="submit"
              variant="contained"
              sx={{
                bgcolor: "#1976d2",
                borderRadius: "30px",
                color: "white",
                fontSize: "1rem",
                padding: "10px",
                height: "56px",
                flex: 1,
                "&:hover": { bgcolor: "#1565c0" },
              }}
              disabled={loading}
            >
              {loading ? "Se încarcă..." : "Salvează modificările"}
            </Button>

            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                color: darkMode ? "#fff" : "#000",
                borderColor: darkMode ? "#fff" : "#000",
                borderRadius: "30px",
                fontSize: "1rem",
                padding: "10px",
                height: "56px",
                flex: 1,
                "&:hover": { borderColor: darkMode ? "#ccc" : "#bbb" },
              }}
            >
              Închide
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCustomerModal;
